/**
 * PlatformSettingsPage
 *
 * Admin platform-level settings at /admin/settings.
 * Currently exposes the STT mode toggle (batch vs. streaming).
 *
 * Pattern: wraps itself in <AdminLayout> (same as AdminDashboardPage, F-404).
 * Uses TanStack Query for GET and PUT /admin/settings/stt-mode.
 */

import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { AdminLayout } from '@/layouts/AdminLayout';
import { api } from '@/lib/api';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type SttMode = 'batch' | 'streaming' | 'full_stream';

interface SttModeResponse {
  mode: SttMode;
}

// ---------------------------------------------------------------------------
// Loading skeleton
// ---------------------------------------------------------------------------

function SettingsSkeleton() {
  return (
    <div className="clinical-card p-6 space-y-4">
      <div className="skeleton h-5 w-48 rounded" />
      <div className="skeleton h-4 w-80 rounded" />
      <div className="flex items-center gap-3 pt-2">
        <div className="skeleton h-6 w-24 rounded-full" />
      </div>
      <div className="flex gap-3 pt-2">
        <div className="skeleton h-11 w-28 rounded-clinical" />
        <div className="skeleton h-11 w-28 rounded-clinical" />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Mode badge
// ---------------------------------------------------------------------------

function ModeBadge({ mode }: { mode: SttMode }) {
  if (mode === 'streaming') {
    return (
      <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-teal-50 text-teal-700 border border-teal-200">
        <span className="w-1.5 h-1.5 rounded-full bg-clinical-teal animate-pulse" aria-hidden="true" />
        Streaming
      </span>
    );
  }
  if (mode === 'full_stream') {
    return (
      <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-purple-50 text-purple-700 border border-purple-200">
        <span className="w-1.5 h-1.5 rounded-full bg-purple-500 animate-pulse" aria-hidden="true" />
        Full Stream
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-gray-100 text-gray-600 border border-gray-200">
      <span className="w-1.5 h-1.5 rounded-full bg-gray-400" aria-hidden="true" />
      Batch
    </span>
  );
}

// ---------------------------------------------------------------------------
// Main Page
// ---------------------------------------------------------------------------

export function PlatformSettingsPage() {
  const queryClient = useQueryClient();
  const [successMsg, setSuccessMsg] = useState<string | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const { data, isLoading, isError } = useQuery<SttModeResponse>({
    queryKey: ['admin', 'settings', 'stt-mode'],
    queryFn: () => api.get<SttModeResponse>('/admin/settings/stt-mode'),
  });

  const mutation = useMutation({
    mutationFn: (mode: SttMode) =>
      api.put<SttModeResponse>('/admin/settings/stt-mode', { mode }),
    onSuccess: (_data, mode) => {
      void queryClient.invalidateQueries({ queryKey: ['admin', 'settings', 'stt-mode'] });
      const modeLabel = mode === 'streaming' ? 'Streaming' : mode === 'full_stream' ? 'Full Stream' : 'Batch';
      setSuccessMsg(`STT mode updated to ${modeLabel}.`);
      setErrorMsg(null);
      setTimeout(() => setSuccessMsg(null), 4_000);
    },
    onError: (err: unknown) => {
      setErrorMsg(err instanceof Error ? err.message : 'Failed to update STT mode.');
      setSuccessMsg(null);
    },
  });

  const currentMode = data?.mode ?? 'batch';

  const handleModeChange = (mode: SttMode) => {
    if (mode === currentMode || mutation.isPending) return;
    mutation.mutate(mode);
  };

  return (
    <AdminLayout>
      {/* Page header */}
      <div className="mb-8">
        <h1 className="font-heading text-2xl font-bold text-clinical-charcoal">
          Platform Settings
        </h1>
        <p className="text-sm font-body text-clinical-text-muted mt-1">
          Global configuration that applies across all hospitals and sessions.
        </p>
      </div>

      {/* Settings card */}
      {isLoading ? (
        <SettingsSkeleton />
      ) : isError ? (
        <div className="clinical-card p-6">
          <p className="text-sm font-body text-red-600">
            Failed to load platform settings. Please refresh the page.
          </p>
        </div>
      ) : (
        <div className="clinical-card p-6 max-w-2xl">
          {/* Section: STT Mode */}
          <div>
            <div className="mb-4">
              <div className="flex items-center gap-3 mb-1">
                <h2 className="font-heading text-base font-semibold text-clinical-charcoal">
                  Speech-to-Text Mode
                </h2>
                <ModeBadge mode={currentMode} />
              </div>
              <p className="text-sm font-body text-clinical-text-muted">
                Controls how audio is processed during live consultations. Streaming mode delivers
                real-time interim results with a ghost bubble; batch mode processes complete
                utterances and is the stable fallback.
              </p>
            </div>

            {/* Mode comparison */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-5">
              <div className={[
                'rounded-clinical border p-4 transition-all',
                currentMode === 'batch'
                  ? 'border-clinical-charcoal bg-clinical-cream-dark'
                  : 'border-clinical-border bg-white',
              ].join(' ')}>
                <p className="text-sm font-body font-semibold text-clinical-charcoal mb-1">
                  Batch
                </p>
                <p className="text-xs font-body text-clinical-text-muted">
                  Processes complete utterances after silence detection. Higher accuracy,
                  no interim results. Stable fallback mode.
                </p>
              </div>
              <div className={[
                'rounded-clinical border p-4 transition-all',
                currentMode === 'streaming'
                  ? 'border-clinical-teal bg-teal-50/50'
                  : 'border-clinical-border bg-white',
              ].join(' ')}>
                <p className="text-sm font-body font-semibold text-clinical-charcoal mb-1">
                  Streaming
                </p>
                <p className="text-xs font-body text-clinical-text-muted">
                  Real-time interim transcription with progressive translation.
                  Ghost bubbles show live results as the speaker talks.
                </p>
              </div>
              <div className={[
                'rounded-clinical border p-4 transition-all',
                currentMode === 'full_stream'
                  ? 'border-purple-400 bg-purple-50/50'
                  : 'border-clinical-border bg-white',
              ].join(' ')}>
                <p className="text-sm font-body font-semibold text-clinical-charcoal mb-1">
                  Full Stream
                </p>
                <p className="text-xs font-body text-clinical-text-muted">
                  Complete utterance capture with LLM-powered translation.
                  Combines streaming interim display with high-quality batch translation.
                </p>
              </div>
            </div>

            {/* Toggle buttons */}
            <div className="flex gap-3 flex-wrap">
              <button
                type="button"
                onClick={() => handleModeChange('batch')}
                disabled={mutation.isPending}
                aria-pressed={currentMode === 'batch'}
                className={[
                  'inline-flex items-center justify-center px-5 py-2.5 rounded-clinical',
                  'font-body font-medium text-sm transition-colors',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-clinical-teal focus-visible:ring-offset-2',
                  'disabled:opacity-50 disabled:cursor-not-allowed',
                  'min-h-[44px]',
                  currentMode === 'batch'
                    ? 'btn-primary'
                    : 'btn-secondary',
                ].join(' ')}
              >
                {mutation.isPending && mutation.variables === 'batch' ? (
                  <span className="flex items-center gap-2">
                    <span className="w-3.5 h-3.5 border-2 border-current border-t-transparent rounded-full animate-spin" aria-hidden="true" />
                    Saving...
                  </span>
                ) : (
                  'Batch'
                )}
              </button>

              <button
                type="button"
                onClick={() => handleModeChange('streaming')}
                disabled={mutation.isPending}
                aria-pressed={currentMode === 'streaming'}
                className={[
                  'inline-flex items-center justify-center px-5 py-2.5 rounded-clinical',
                  'font-body font-medium text-sm transition-colors',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-clinical-teal focus-visible:ring-offset-2',
                  'disabled:opacity-50 disabled:cursor-not-allowed',
                  'min-h-[44px]',
                  currentMode === 'streaming'
                    ? 'btn-primary'
                    : 'btn-secondary',
                ].join(' ')}
              >
                {mutation.isPending && mutation.variables === 'streaming' ? (
                  <span className="flex items-center gap-2">
                    <span className="w-3.5 h-3.5 border-2 border-current border-t-transparent rounded-full animate-spin" aria-hidden="true" />
                    Saving...
                  </span>
                ) : (
                  'Streaming'
                )}
              </button>

              <button
                type="button"
                onClick={() => handleModeChange('full_stream')}
                disabled={mutation.isPending}
                aria-pressed={currentMode === 'full_stream'}
                className={[
                  'inline-flex items-center justify-center px-5 py-2.5 rounded-clinical',
                  'font-body font-medium text-sm transition-colors',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-clinical-teal focus-visible:ring-offset-2',
                  'disabled:opacity-50 disabled:cursor-not-allowed',
                  'min-h-[44px]',
                  currentMode === 'full_stream'
                    ? 'btn-primary'
                    : 'btn-secondary',
                ].join(' ')}
              >
                {mutation.isPending && mutation.variables === 'full_stream' ? (
                  <span className="flex items-center gap-2">
                    <span className="w-3.5 h-3.5 border-2 border-current border-t-transparent rounded-full animate-spin" aria-hidden="true" />
                    Saving...
                  </span>
                ) : (
                  'Full Stream'
                )}
              </button>
            </div>

            {/* Success / error feedback */}
            {successMsg && (
              <div
                role="status"
                aria-live="polite"
                className="mt-4 rounded-clinical border border-teal-200 bg-teal-50 px-4 py-3"
              >
                <p className="text-sm font-body text-teal-700">{successMsg}</p>
              </div>
            )}
            {errorMsg && (
              <div
                role="alert"
                className="mt-4 rounded-clinical border border-red-200 bg-red-50 px-4 py-3"
              >
                <p className="text-sm font-body text-red-700">{errorMsg}</p>
              </div>
            )}
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
